/**
 * Validação e conversão dos formulários do cadastro de máquina.
 */
import {
  MACHINE_PROTECTION_TYPES,
  MachineAdequacyFormData,
  MachineAdequacyPriority,
  MachineAdequacyProgress,
  MachineCreateData,
  MachineProtectionFormData,
  MachineProtectionState,
  MachineProtectionType,
} from '../../../../types/machine';
import { IdentificationErrors, IdentificationValues } from './MachineWizard.types';

const blankToNull = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
};

export function validateIdentification(
  values: IdentificationValues,
  requiresCompany = false
): IdentificationErrors {
  const errors: IdentificationErrors = {};

  if (requiresCompany && values.companyId === '') errors.companyId = 'Escolha a empresa dona da máquina.';

  if (values.name.trim() === '') errors.name = 'Diga o nome da máquina.';
  else if (values.name.trim().length > 200) errors.name = 'O nome pode ter até 200 letras.';

  if (values.tag.length > 100) errors.tag = 'Use até 100 letras.';
  if (values.serialNumber.length > 100) errors.serialNumber = 'Use até 100 letras.';
  if (values.maintenanceFrequency.length > 100) errors.maintenanceFrequency = 'Use até 100 letras.';

  if (values.yearOfManufacture.trim() !== '') {
    const year = Number(values.yearOfManufacture);
    const limit = new Date().getFullYear() + 1;
    if (!Number.isInteger(year) || year < 1900 || year > limit) {
      errors.yearOfManufacture = `Informe um ano entre 1900 e ${limit}.`;
    }
  }

  return errors;
}

/** `includeCompany` é falso na atualização: a empresa dona não muda depois que a máquina é salva. */
export function identificationToPayload(values: IdentificationValues, includeCompany = true): MachineCreateData {
  return {
    ...(!includeCompany || values.companyId === '' ? {} : { companyId: Number(values.companyId) }),
    name: values.name.trim(),
    tag: blankToNull(values.tag),
    departmentId: values.departmentId === '' ? null : Number(values.departmentId),
    sourceGeneratorId: values.sourceGenerator ? Number(values.sourceGenerator.id) : null,
    manufacturer: blankToNull(values.manufacturer),
    model: blankToNull(values.model),
    serialNumber: blankToNull(values.serialNumber),
    yearOfManufacture: values.yearOfManufacture.trim() === '' ? null : Number(values.yearOfManufacture),
    location: blankToNull(values.location),
    status: values.status,
    observations: blankToNull(values.observations),
    maintenanceFrequency: blankToNull(values.maintenanceFrequency),
    lastMaintenanceDate: blankToNull(values.lastMaintenanceDate),
    nextMaintenanceDate: blankToNull(values.nextMaintenanceDate),
  };
}

export interface ProtectionDraft {
  protectionType: MachineProtectionType | '';
  description: string;
  location: string;
  state: MachineProtectionState;
  observations: string;
}

export type ProtectionErrors = Partial<Record<keyof ProtectionDraft, string>>;

export const EMPTY_PROTECTION_DRAFT: ProtectionDraft = {
  protectionType: '',
  description: '',
  location: '',
  state: 'CONFORME',
  observations: '',
};

export function validateProtection(draft: ProtectionDraft): ProtectionErrors {
  const errors: ProtectionErrors = {};

  if (draft.protectionType === '') {
    errors.protectionType = 'Escolha o tipo de proteção na lista.';
  } else if (!MACHINE_PROTECTION_TYPES.includes(draft.protectionType)) {
    errors.protectionType = 'Escolha um tipo da lista.';
  } else if (draft.protectionType === 'OUTRA' && draft.description.trim() === '') {
    errors.description = 'Descreva a proteção, porque você escolheu "Outra".';
  }

  if (draft.location.length > 255) errors.location = 'Use até 255 letras.';
  return errors;
}

export function protectionToPayload(draft: ProtectionDraft): MachineProtectionFormData {
  return {
    protectionType: draft.protectionType as MachineProtectionType,
    description: draft.protectionType === 'OUTRA' ? draft.description.trim() : blankToNull(draft.description),
    location: blankToNull(draft.location),
    state: draft.state,
    observations: blankToNull(draft.observations),
  };
}

export interface AdequacyDraft {
  description: string;
  priority: MachineAdequacyPriority;
  dueDate: string;
  responsible: string;
  progress: MachineAdequacyProgress;
  hazardAssessmentId: string;
}

export type AdequacyErrors = Partial<Record<keyof AdequacyDraft, string>>;

export const EMPTY_ADEQUACY_DRAFT: AdequacyDraft = {
  description: '',
  priority: 'MEDIA',
  dueDate: '',
  responsible: '',
  progress: 'NAO_INICIADA',
  hazardAssessmentId: '',
};

export function validateAdequacy(draft: AdequacyDraft): AdequacyErrors {
  const errors: AdequacyErrors = {};
  if (draft.description.trim() === '') errors.description = 'Diga o que precisa ser feito.';
  if (draft.responsible.length > 200) errors.responsible = 'Use até 200 letras.';
  return errors;
}

export function adequacyToPayload(draft: AdequacyDraft): MachineAdequacyFormData {
  return {
    description: draft.description.trim(),
    priority: draft.priority,
    dueDate: blankToNull(draft.dueDate),
    responsible: blankToNull(draft.responsible),
    progress: draft.progress,
    hazardAssessmentId: draft.hazardAssessmentId === '' ? null : Number(draft.hazardAssessmentId),
  };
}

/** `2026-11-30` → `30/11/2026`, sem passar por `Date` (que deslocaria o dia pelo fuso). */
export function formatDate(iso: string | null): string {
  if (!iso) return 'Sem prazo';
  const [year, month, day] = iso.slice(0, 10).split('-');
  return `${day}/${month}/${year}`;
}

export function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}
